import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * MicroCheckpoint.js - Inline MCQ between cards
 * Shows 3 options, validates answer, explains result
 */

export default function MicroCheckpoint({
  checkpoint,           // { question, options: [{text, correct}, ...], explanation }
  onAnswered = () => {},  // callback(isCorrect, selectedOption)
  isLoading = false
}) {
  const { colors, isDark } = useTheme();
  const [selectedOption, setSelectedOption] = useState(null);
  const [answered, setAnswered] = useState(false);

  if (!checkpoint) return null;

  const handleSelectOption = (idx) => {
    if (answered) return;
    setSelectedOption(idx);
    setAnswered(true);
  };

  const isCorrect = answered && selectedOption !== null && checkpoint.options[selectedOption]?.correct;
  const correctIdx = checkpoint.options.findIndex(o => o.correct);

  return (
    <div className="app-card p-4 sm:p-6 rounded-lg border-2 my-4 sm:my-6 w-full max-w-2xl mx-auto" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderColor: colors.border }}>
      {/* Header */}
      <h4 className="text-xs sm:text-sm font-bold mb-3 sm:mb-4 uppercase tracking-wide" style={{ color: colors.accent }}>
        💡 Micro-Checkpoint
      </h4>

      {/* Question */}
      <p className="font-semibold text-base sm:text-lg mb-4 sm:mb-6 leading-tight" style={{ color: colors.primaryText }}>
        {checkpoint.question}
      </p>

      {/* Options */}
      <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
        {checkpoint.options.map((option, idx) => {
          const isSelected = selectedOption === idx;
          const isCorrectOption = option.correct;

          let bgColor = colors.background;
          let borderColor = colors.border;
          let labelBg = 'transparent';
          let labelBorder = colors.border;
          let labelColor = colors.secondaryText;
          let labelChar = String.fromCharCode(65 + idx);
          let opacity = 1;

          if (answered) {
            if (isSelected && isCorrect) {
              bgColor = '#16a34a18';
              borderColor = '#16a34a';
              labelBg = '#16a34a';
              labelBorder = '#16a34a';
              labelColor = '#fff';
              labelChar = 'OK';
            } else if (isSelected && !isCorrect) {
              bgColor = '#dc262618';
              borderColor = '#dc2626';
              labelBg = '#dc2626';
              labelBorder = '#dc2626';
              labelColor = '#fff';
              labelChar = 'X';
            } else if (!isSelected && isCorrectOption) {
              bgColor = '#16a34a10';
              borderColor = '#16a34a';
              labelBg = '#16a34a';
              labelBorder = '#16a34a';
              labelColor = '#fff';
              labelChar = 'OK';
            } else {
              opacity = 0.4;
            }
          }

          return (
            <button
              key={idx}
              onClick={() => handleSelectOption(idx)}
              disabled={answered || isLoading}
              className="w-full p-3 sm:p-4 text-left rounded-lg border-2 transition-all text-sm sm:text-base"
              style={{
                backgroundColor: bgColor,
                borderColor,
                color: colors.primaryText,
                opacity,
                cursor: answered || isLoading ? 'default' : 'pointer'
              }}
            >
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs sm:text-sm font-bold"
                  style={{ backgroundColor: labelBg, borderColor: labelBorder, color: labelColor }}>
                  <span>{labelChar}</span>
                </div>
                <span className="flex-1 text-sm sm:text-base">{typeof option === 'string' ? option : option.text}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Result feedback */}
      {answered && (
        <div className="p-3 sm:p-4 rounded-lg border-l-4 mt-4 sm:mt-6" style={{
          borderLeftColor: isCorrect ? '#16a34a' : '#dc2626',
          backgroundColor: isCorrect ? '#16a34a12' : '#dc262612',
        }}>
          <p className="text-xs sm:text-sm font-semibold mb-1" style={{ color: isCorrect ? '#16a34a' : '#dc2626' }}>
            {isCorrect ? 'OK Correct!' : 'X Not quite'}
          </p>
          <p className="text-xs sm:text-sm leading-relaxed" style={{ color: colors.primaryText }}>{checkpoint.explanation}</p>
        </div>
      )}

      {/* Continue button */}
      {answered && (
        <button
          onClick={() => onAnswered(isCorrect, selectedOption)}
          className="w-full mt-4 sm:mt-6 py-2 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base"
          style={{ backgroundColor: isCorrect ? '#16a34a' : colors.accent, color: '#fff' }}
        >
          Continue ->
        </button>
      )}
    </div>
  );
}
// reload
// v2

